import type { DrawingSet, DrawingSetType, Project, drawingPage } from 'reduxStore/projects/types'
import { getSharedDrawingSets, shareTokenFor } from './ShareLinkApi'
import { axiosInstance } from './axiosInstance'
import { END_POINT_PRODUCT } from './index'

export interface CreateDrawingSetRequest {
  projectId: string
  name: string
  setType: DrawingSetType
  comparedAgainstSetId: string | null
  /** YYYY-MM-DD */
  issueDate?: string
}

// originalSet is non-null only when the backend just materialized the
// ORIGINAL set for a project that had none (first revision upload).
export interface CreateDrawingSetResponse {
  createdSet: DrawingSet
  originalSet: DrawingSet | null
}

const BASE = `${END_POINT_PRODUCT}/api/drawing-set`

export const getDrawingSets = (projectId: string) => {
  const shareToken = shareTokenFor(projectId)
  if (shareToken) return getSharedDrawingSets(shareToken)
  return axiosInstance.request<DrawingSet[]>({
    method: 'get',
    url: `${BASE}/${projectId}`,
  })
}

export const saveDrawingSet = (drawingSet: CreateDrawingSetRequest) => {
  return axiosInstance.request<CreateDrawingSetResponse>({
    method: 'post',
    url: BASE,
    data: drawingSet,
  })
}

export const updateDrawingSet = (drawingSet: DrawingSet) => {
  return axiosInstance.request<DrawingSet>({
    method: 'put',
    url: BASE,
    data: drawingSet,
  })
}

export const deleteDrawingSetRequest = (setId: string) => {
  return axiosInstance.request<void>({
    method: 'delete',
    url: `${BASE}/${setId}`,
  })
}

// "Use for takeoff": the set becomes the working set; a related new issue moves the old one into history.
export const putAcceptDrawingSet = (setId: string) => {
  return axiosInstance.request<Project>({
    method: 'put',
    url: `${BASE}/${setId}/accept`,
  })
}

// Copies a new issue's unchanged sheets in from the set it was based on (no takeoff; that carries on accept).
export const postCarryForward = (setId: string, sourceSetId: string, pageIds: string[]) => {
  return axiosInstance.request<drawingPage[]>({
    method: 'post',
    url: `${BASE}/${setId}/carry-forward`,
    data: { sourceSetId, pageIds },
  })
}

export const putActiveDrawingSet = (projectId: string, drawingSetId: string | null) => {
  return axiosInstance.request<Project>({
    method: 'put',
    url: `${END_POINT_PRODUCT}/api/project/${projectId}/active-drawing-set`,
    data: { drawingSetId },
  })
}
